var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useAsyncEffect, useAsyncState, wrap } from '@hilma/tools';
import createMuiTheme from '@material-ui/core/styles/createMuiTheme';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import StylesProvider from '@material-ui/styles/StylesProvider';
import jssPreset from '@material-ui/styles/jssPreset';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import heLocale from 'date-fns/locale/he';
import enLocale from 'date-fns/locale/en-US';
import { create } from 'jss';
import rtl from 'jss-rtl';
import { Language } from '../types';
import { themeColor, error, i18n as baseI18n, fadedBrightThemeColor } from '../constants';
import { usePathLanguage } from '../hooks';
import { isLanguage } from '../functions';
import { languages } from '../constants';
export var LanguageContext = createContext(null);
export var TranslateContext = createContext(null);
export var TransformObjectContext = createContext(null);
export var TranslatedI18nContext = createContext(null);
export var IsI18nRouterContext = createContext(null);
var I18nProvider = function (_a) {
    var _b = _a.i18n, i18n = _b === void 0 ? baseI18n : _b, _c = _a.router, router = _c === void 0 ? true : _c, muiPalette = _a.muiPalette, children = _a.children;
    var pathLanguage = usePathLanguage();
    var _d = useAsyncState(function () {
        var language = pathLanguage || localStorage.getItem('language');
        if (!isLanguage(language))
            language = Language.He;
        localStorage.setItem('language', language);
        return router ? language : Language.He;
    }), language = _d[0], setLanguage = _d[1];
    var theme = useMemo(function () { return createMuiTheme({
        direction: language === Language.He ? 'rtl' : 'ltr',
        palette: muiPalette || {
            primary: {
                main: themeColor
            },
            secondary: {
                // main: lightThemeColor
                main: fadedBrightThemeColor
            },
            error: {
                main: error
            }
        }
    }); }, [language, muiPalette]);
    var jss = useMemo(function () { return language === Language.He ? create({ plugins: __spreadArray(__spreadArray([], jssPreset().plugins), [rtl()]) }) : create({ plugins: __spreadArray([], jssPreset().plugins) }); }, [language]);
    var translatedI18n = useMemo(function () {
        var translatedI18n = {};
        for (var key in i18n) {
            if (Object.prototype.hasOwnProperty.call(i18n, key)) {
                translatedI18n[key] = i18n[key][language];
            }
        }
        return translatedI18n;
    }, [language]);
    var translate = useCallback(function (key) {
        try {
            if (typeof key === "function")
                return key(translatedI18n);
            var _a = key.split('.'), mainKey = _a[0], secondaryKey = _a[1], keys = _a.slice(2);
            var mainResource = i18n[mainKey][language];
            var text = keys.reduce(function (previousValue, currentValue) {
                return previousValue[currentValue];
            }, mainResource[secondaryKey]);
            return text;
        }
        catch (error) {
            return '';
        }
    }, [language, translatedI18n]);
    var transformObject = useCallback(function (object) {
        var newObject = {};
        for (var key in object) {
            var keyLanguage = key.substring(0, 2);
            if (!isLanguage(keyLanguage)) {
                newObject[key] = object[key];
                continue;
            }
            if (keyLanguage === language && key.charAt(2) && key.charAt(2) === key.charAt(2).toUpperCase())
                newObject["" + key.charAt(2).toLowerCase() + key.slice(3)] = object[key];
        }
        return newObject;
    }, [language]);
    useAsyncEffect(function () { return __awaiter(void 0, void 0, void 0, function () {
        var newLanguage, newLanguage_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!router)
                        return [2 /*return*/];
                    newLanguage = pathLanguage || language;
                    if (!(newLanguage && (newLanguage === Language.En || newLanguage === Language.He))) return [3 /*break*/, 2];
                    return [4 /*yield*/, setLanguage(newLanguage)];
                case 1:
                    _a.sent();
                    localStorage.setItem('language', newLanguage);
                    return [3 /*break*/, 4];
                case 2: return [4 /*yield*/, setLanguage(function (language) {
                        if (language === Language.En)
                            return Language.He;
                        return Language.En;
                    })];
                case 3:
                    newLanguage_1 = _a.sent();
                    localStorage.setItem('language', newLanguage_1);
                    _a.label = 4;
                case 4: return [2 /*return*/];
            }
        });
    }); }, [pathLanguage]);
    var wrapper = wrap([ThemeProvider, { theme: theme }], [StylesProvider, { jss: jss }], [MuiPickersUtilsProvider, { utils: DateFnsUtils, locale: language === Language.He ? heLocale : enLocale }], [LanguageContext.Provider, { value: language }], [TranslateContext.Provider, { value: translate }], [TransformObjectContext.Provider, { value: transformObject }], [TranslatedI18nContext.Provider, { value: translatedI18n }], [IsI18nRouterContext.Provider, { value: router }]);
    if (!router)
        return wrapper(children);
    return wrapper(_jsxs(Switch, { children: [_jsx(Route, { path: "/", exact: true, render: function () { return _jsx(Redirect, { to: "/" + language + "/" }, void 0); } }, void 0),
            languages.map(function (language) { return (_jsx(Route, { path: "/" + language + "/*", render: function () { return children; } }, language)); }),
            languages.map(function (language) { return (_jsx(Route, { exact: true, path: "/" + language, render: function () { return children; } }, language)); }),
            _jsx(Route, { path: "*", exact: true, render: function () { return _jsx(Redirect, { to: "/" + language + "/page-not-found" }, void 0); } }, void 0)] }, void 0));
};
export default I18nProvider;
//# sourceMappingURL=I18nProvider.js.map